import type { AuthorInfo } from "@serophin/nottldr-shared";
import { useFollowing } from "@serophin/nottldr-shared/hooks";
import { Search, X } from "lucide-preact";
import { useState } from "preact/hooks";
import { PillPicker } from "../explore/PillPicker";
import { AuthorCard } from "./AuthorCard";

export type AuthorListProps = {
  authors: AuthorInfo[];
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
};

type FollowFilter = "all" | "following" | "not-following";

const FOLLOW_FILTER_OPTIONS: Record<FollowFilter, string> = { all: "All", following: "Following", "not-following": "Not Following" };

const ICON_BUTTON_CLASS =
  "p-1 rounded-full bg-stone-100 text-stone-700 hover:bg-stone-200 dark:bg-stone-800 dark:text-stone-300 dark:hover:bg-stone-700 transition-all duration-200";

const SPINNER = <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600" />;

type ToolbarProps = { followFilter: FollowFilter; onFollowFilter: (filter: FollowFilter) => void; query: string; onQuery: (query: string) => void };

/** The follow filter (signed-in visitors only) and a name search that opens in its place. */
const Toolbar = ({ followFilter, onFollowFilter, query, onQuery }: ToolbarProps) => {
  const [showSearch, setShowSearch] = useState(false);

  if (showSearch) {
    return (
      <div class="mt-4 mb-4 flex items-center gap-2">
        <div class="flex-1 relative">
          <input
            type="text"
            placeholder="Search authors by name..."
            value={query}
            onInput={(event) => onQuery(event.currentTarget.value)}
            class="w-full px-2 py-1 text-xs rounded-full border border-gray-300 dark:border-gray-600 bg-white dark:bg-neutral-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
            autoFocus
          />
        </div>
        <button
          type="button"
          onClick={() => {
            setShowSearch(false);
            onQuery("");
          }}
          class={ICON_BUTTON_CLASS}
          aria-label="Close search"
        >
          <X size={16} />
        </button>
      </div>
    );
  }

  return (
    <div class="mt-4 mb-4 flex items-center gap-2">
      <div class="flex-1 hidden-anonymous">
        <PillPicker
          id="author-follow-filter"
          small
          value={followFilter}
          onChange={(value) => onFollowFilter(value as FollowFilter)}
          values={FOLLOW_FILTER_OPTIONS}
        />
      </div>
      <button type="button" onClick={() => setShowSearch(true)} class={ICON_BUTTON_CLASS} aria-label="Search authors">
        <Search size={16} />
      </button>
    </div>
  );
};

const byFollowFilter = (authors: AuthorInfo[], filter: FollowFilter, following: Set<string>): AuthorInfo[] => {
  if (filter === "following") {
    return authors.filter((author) => following.has(author.alias));
  }
  return filter === "not-following" ? authors.filter((author) => !following.has(author.alias)) : authors;
};

export const AuthorList = ({ authors, hasMore, isLoading, onLoadMore }: AuthorListProps) => {
  const [followFilter, setFollowFilter] = useState<FollowFilter>("all");
  const [query, setQuery] = useState("");
  const { following } = useFollowing();

  const byName = query ? authors.filter((author) => author.alias.toLowerCase().includes(query.toLowerCase())) : authors;
  const shown = byFollowFilter(byName, followFilter, following);

  return (
    <div>
      <Toolbar followFilter={followFilter} onFollowFilter={setFollowFilter} query={query} onQuery={setQuery} />
      {isLoading && authors.length === 0 && <div class="flex justify-center items-center py-16">{SPINNER}</div>}
      {authors.length > 0 && (
        <>
          <div class="mt-5 space-y-6">
            {shown.length === 0 && !isLoading && <div class="text-center py-5 text-neutral-500">No authors found</div>}
            {shown.map((author, index) => (
              <AuthorCard key={author.alias} author={author} priority={index < 2} />
            ))}
          </div>
          {isLoading && <div class="flex justify-center items-center py-8">{SPINNER}</div>}
          {!isLoading && (
            <div class="text-center py-5">
              {!hasMore && <div class="text-gray-500 text-center my-5">No more authors</div>}
              {hasMore && (
                <button type="button" onClick={onLoadMore} class="w-full px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors">
                  Load More
                </button>
              )}
            </div>
          )}
        </>
      )}
      {!isLoading && authors.length === 0 && <div class="text-center py-5 text-neutral-500">No authors found</div>}
    </div>
  );
};
